/**
 * Bottom-right "New version available" toast. Appears when useVersionWatcher
 * detects /version.json has changed since this tab booted. Two paths:
 *  - "Reload" → window.location.reload(), forces a fresh index.html fetch
 *    which pulls the new content-hashed bundle.
 *  - "Later" → dismisses for the current latest version. If a yet-newer
 *    deploy happens after dismissal, the toast comes back because
 *    `dismissedVersion` won't match the new latest.
 *
 * Deliberately not a modal — never blocks input. The 2026-05-07 callable
 * schema change is the precise scenario this exists to handle (stale tab
 * fails with invalid-argument), so the toast nudges, but the user stays
 * in control of when to reload.
 */

import { useState } from 'react';
import { useVersionWatcher } from '../../hooks/useVersionWatcher';

export function VersionUpdateToast() {
  const { updateAvailable, latestVersion } = useVersionWatcher();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  // Hide while no update, or while dismissed for the current latest.
  if (!updateAvailable) return null;
  if (latestVersion && dismissedVersion === latestVersion) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 10_000,
        background: 'var(--dark-2, #1a1a1a)',
        border: '1px solid var(--gold, #c8a84e)',
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        maxWidth: '340px',
        color: 'var(--white, #fff)',
        fontSize: '0.9rem',
        lineHeight: 1.4,
      }}
    >
      <div style={{ fontWeight: 700, color: 'var(--gold, #c8a84e)', marginBottom: '4px' }}>
        New version available
      </div>
      <div style={{ color: 'var(--gray-300, #ccc)', marginBottom: '12px' }}>
        Reload to pick up the latest portal changes. Your work in this tab will be lost — finish anything in progress first.
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => latestVersion && setDismissedVersion(latestVersion)}
          style={{ padding: '6px 12px', fontSize: '0.85rem' }}
        >
          Later
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.location.reload()}
          style={{ padding: '6px 12px', fontSize: '0.85rem' }}
        >
          Reload now
        </button>
      </div>
    </div>
  );
}
